import React, { useState, useEffect, useRef } from "react";
import { ShoppingCart, Truck, BadgeCheck, MessageCircle, ChevronLeft, ChevronRight, ArrowDown } from "lucide-react";
import { BsWhatsapp } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import CategoriesPage from "./CategoriesPage";
// components/VideoDialog.jsx
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "../components/ui/dialog";
import { X } from "lucide-react"; // optional close icon
import SEO from "./SEO";


const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Store",
  name: "Dar Oulmès Store",
  url: "https://daroulmes.store",
  image: "https://daroulmes.store/og-image.jpg",
  description:
    "Local ecommerce store in Oulmès offering jewellery, clothes, kitchen essentials, and makeup at reasonable prices.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Oulmès",
    addressCountry: "MA",
  },
  areaServed: {
    "@type": "Place",
    name: "Oulmès, Morocco",
  },
  sameAs: [
    "https://daroulmes.store"
  ],
};

function useProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API}/api/products?limit=20`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setProducts(list);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);
  return { products, loading };
}

// Hero slide keys — text comes from translation files
const heroSlideKeys = [
  { img: "https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912956/ChatGPT_Image_4_avr._2026_02_32_43.png", headingKey: "hero.slide1_heading", subKey: "hero.slide1_sub", ctaKey: "hero.slide1_cta" },
  { img: "https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912807/ChatGPT_Image_4_avr._2026_02_29_56.png", headingKey: "hero.slide2_heading", subKey: "hero.slide2_sub", ctaKey: "hero.slide2_cta" },
  { img: "https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912874/Gemini_Generated_Image_rhhoxerhhoxerhho.png", headingKey: "hero.slide3_heading", subKey: "hero.slide3_sub", ctaKey: "hero.slide3_cta" },
  { img: "https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912892/Gemini_Generated_Image_79sqk279sqk279sq.png", headingKey: "hero.slide4_heading", subKey: "hero.slide4_sub", ctaKey: "hero.slide4_cta" },
  { img: "https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912786/kitchen_stuff.png", headingKey: "hero.slide5_heading", subKey: "hero.slide5_sub", ctaKey: "hero.slide5_cta" },
];

const TAG_COLORS = {
  "tags.bestseller": { bg: "#dfcc25", color: "#1f3d3a" },
  "tags.new":        { bg: "#c8a98a", color: "#fff" },
  "tags.hot":        { bg: "#1f3d3a", color: "#dfcc25" },
};


export function VideoDialog({ videoUrl, buttonText = "Watch Demo", buttonClassName = "" }) {
  // Detect if URL is YouTube/Vimeo for embed
  const { t, i18n } = useTranslation();

  const getEmbedUrl = (url) => {
    // YouTube
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    // Assume direct video file
    return url;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  const isEmbed = embedUrl !== videoUrl;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className={`group relative px-10 py-3 text-white transition-all duration-500 overflow-hidden ${buttonClassName}`}
          style={{
            fontFamily: "Poiret One",
            border: "1px solid rgba(255,255,255,0.25)",
            letterSpacing: "0.2em",
            fontSize: "13px",
            textTransform: "uppercase",
            backdropFilter: "blur(10px)",
            background: "rgba(255,255,255,0.05)",
          }}
        >
          {/* Button hover fill */}
          <span
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: "rgba(255,182,193,0.2)" }}
          />
          {/* Corner accents */}
          <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-pink-300/60" />
          <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-pink-300/60" />
          <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-pink-300/60" />
          <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-pink-300/60" />
          <span className="relative">{buttonText}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-black/90 border border-white/20">
        <DialogHeader className="p-4 border-b border-white/10">
          <DialogTitle className="text-white font-light tracking-wide">{t("homeExtra.video_title")}</DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4 text-white" />
            <span className="sr-only">{t("homeExtra.close")}</span>
          </DialogClose>
        </DialogHeader>
        <div className="relative w-full aspect-video">
          {isEmbed ? (
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={t("homeExtra.video_title")}
            />
          ) : (
            <video
              src={videoUrl}
              controls
              autoPlay
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrustBadgeItem({ Icon, labelKey, t, isLast }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        paddingTop: "40px",
        paddingBottom: "40px",
        cursor: "default",
        overflow: "hidden",
        borderRight: !isLast ? "1px solid rgba(31,61,58,0.1)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* Sweep fill */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "#1f3d3a",
          transform: hovered ? "translateY(0%)" : "translateY(100%)",
          transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
        }}
      />

      {/* Hexagon icon container */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "52px",
          height: "52px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
          background: hovered ? "rgba(223,204,37,0.15)" : "rgba(31,61,58,0.06)",
          transition: "background 0.3s ease",
        }}
      >
        <Icon
          size={22}
          style={{
            color: hovered ? "#dfcc25" : "#1f3d3a",
            transition: "color 0.3s ease",
          }}
        />
      </div>

      <p
        style={{
          position: "relative",
          zIndex: 10,
          fontSize: "13px",
          fontWeight: 600,
          letterSpacing: "0.05em",
          fontFamily: "Poiret One",
          color: hovered ? "#dfcc25" : "#1f3d3a",
          transition: "color 0.3s ease",
        }}
      >
        {t(labelKey)}
      </p>
    </div>
  );
}

function WhyCard({ card, i, t }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "36px 32px 32px",
        display: "flex",
        flexDirection: "column",
        cursor: "default",
        transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        // Glass dark panel
        background: hovered
          ? "rgba(223,204,37,0.04)"
          : "rgba(255,255,255,0.03)",
        backdropFilter: "blur(16px)",
        border: `1px solid ${hovered ? "rgba(223,204,37,0.25)" : "rgba(255,255,255,0.07)"}`,
        transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      {/* Scan lines inside card */}
      <div
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(255,255,255,0.008) 3px, rgba(255,255,255,0.008) 4px)",
        }}
      />

      {/* Corner brackets */}
      <div style={{ position: "absolute", top: 12, left: 12, width: 14, height: 14, borderTop: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, borderLeft: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, transition: "border-color 0.4s" }} />
      <div style={{ position: "absolute", top: 12, right: 12, width: 14, height: 14, borderTop: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, borderRight: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, transition: "border-color 0.4s" }} />
      <div style={{ position: "absolute", bottom: 12, left: 12, width: 14, height: 14, borderBottom: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, borderLeft: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, transition: "border-color 0.4s" }} />
      <div style={{ position: "absolute", bottom: 12, right: 12, width: 14, height: 14, borderBottom: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, borderRight: `1px solid ${hovered ? "#dfcc25" : "rgba(255,255,255,0.15)"}`, transition: "border-color 0.4s" }} />

      {/* Top glow line */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "1px",
        background: hovered
          ? `linear-gradient(90deg, transparent, #dfcc25, transparent)`
          : `linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)`,
        transition: "background 0.4s",
      }} />

      {/* Index number */}
      <div style={{
        fontSize: "10px",
        letterSpacing: "0.4em",
        color: hovered ? "rgba(223,204,37,0.7)" : "rgba(255,255,255,0.2)",
        fontFamily: "Poiret One",
        marginBottom: "24px",
        transition: "color 0.4s",
      }}>
        {String(i + 1).padStart(2, "0")} ——
      </div>

      {/* Emoji in dark hexagon */}
      <div style={{
        width: "56px", height: "56px",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "24px",
        marginBottom: "20px",
        clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
        background: hovered ? "rgba(223,204,37,0.15)" : "rgba(255,255,255,0.06)",
        transition: "background 0.4s",
        transform: hovered ? "scale(1.1)" : "scale(1)",
        transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {card.emoji}
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: "Poiret One",
        fontSize: "20px",
        fontWeight: 700,
        color: hovered ? "#ffffff" : "rgba(255,255,255,0.8)",
        marginBottom: "12px",
        transition: "color 0.4s",
        letterSpacing: "0.05em",
      }}>
        {t(card.titleKey)}
      </h3>

      {/* Body */}
      <p style={{
        fontSize: "13px",
        lineHeight: 1.8,
        color: "rgba(255,255,255,0.4)",
        flex: 1,
        letterSpacing: "0.02em",
      }}>
        {t(card.bodyKey)}
      </p>

      {/* Bottom dash accent */}
      <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{
          width: hovered ? "32px" : "16px",
          height: "1px",
          background: "#dfcc25",
          opacity: hovered ? 1 : 0.3,
          transition: "all 0.4s",
        }} />
        <div style={{
          width: "4px", height: "4px",
          background: "#dfcc25",
          opacity: hovered ? 1 : 0.3,
          transform: "rotate(45deg)",
          transition: "opacity 0.4s",
        }} />
      </div>

      {/* Bottom radial glow */}
      <div style={{
        position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "80%", height: "60px",
        background: `radial-gradient(ellipse at bottom, rgba(223,204,37,${hovered ? 0.08 : 0}) 0%, transparent 70%)`,
        transition: "background 0.5s",
        pointerEvents: "none",
      }} />
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);
  const { products, loading: productsLoading } = useProducts();

  // !NEW — full-screen splash/loading overlay shown on first mount for 5s
  const [showSplash, setShowSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  useEffect(() => {
    // start fade-out at 4.6s, fully unmount at 5s so transition is visible
    const fadeTimer  = setTimeout(() => setSplashFading(true), 4600);
    const closeTimer = setTimeout(() => setShowSplash(false),   5000);
    return () => { clearTimeout(fadeTimer); clearTimeout(closeTimer); };
  }, []);
  // Lock body scroll while splash is visible
  useEffect(() => {
    if (!showSplash) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [showSplash]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroSlideKeys.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollBy = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 300, behavior: "smooth" });
    }
  };

  // Why cards — built from translation keys
  const whyCards = [
    {
      emoji: "💎",
      titleKey: "why.card1_title",
      bodyKey:  "why.card1_body",
      accent: "#dfcc25",
      accentLight: "rgba(223,204,37,0.12)",
      border: "rgba(223,204,37,0.3)",
    },
    {
      emoji: "⚡",
      titleKey: "why.card2_title",
      bodyKey:  "why.card2_body",
      accent: "#c8a98a",
      accentLight: "rgba(200,169,138,0.14)",
      border: "rgba(200,169,138,0.35)",
    },
    {
      emoji: "🛍️",
      titleKey: "why.card3_title",
      bodyKey:  "why.card3_body",
      accent: "#1f3d3a",
      accentLight: "rgba(31,61,58,0.08)",
      border: "rgba(31,61,58,0.2)",
    },
  ];

  const trustBadges = [
    { Icon: Truck,         labelKey: "trust.fast_delivery" },
    { Icon: ShoppingCart,  labelKey: "trust.pay_on_delivery" },
    { Icon: MessageCircle, labelKey: "trust.whatsapp_confirmation" },
    { Icon: BadgeCheck,    labelKey: "trust.quality_products" },
  ];

  const quickLinks = [
    { labelKey: "footer.link_shop",       href: "#" },
    { labelKey: "footer.link_categories", href: "#categories" },
    { labelKey: "footer.link_about",      href: "#" },
    { labelKey: "footer.link_contact",    href: "#" },
  ];

  const slide = heroSlideKeys[currentIndex];

  return (
    <>
      {/* ══ SPLASH / LOADING OVERLAY (first 5s) ══ */}
      {showSplash && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            opacity: splashFading ? 0 : 1,
            transition: "opacity 400ms ease-out",
            pointerEvents: splashFading ? "none" : "auto",
            overflow: "hidden",
            background: "#0f1918", // fallback while image loads
          }}
        >
          {/* !FIX — image now fills the ENTIRE screen edge-to-edge.
              Using a real <img> with object-fit:fill stretches it to the
              full viewport (no cropping, no centered "cute" thumbnail).
              `background-image: cover` was showing only a slice; `objectFit`
              in CSS doesn't apply to background images. */}
          <img
            src="https://res.cloudinary.com/dxbqh10nq/image/upload/v1777912636/ChatGPT_Image_18_avr._2026_21_11_21.png"
            alt=""
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              objectFit: "fill",   // stretch to fill viewport exactly
              display: "block",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          {/* dark overlay for contrast */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(31,61,58,0.55) 0%, rgba(15,25,24,0.78) 100%)",
              backdropFilter: "blur(2px)",
            }}
          />

          {/* centered content */}
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              textAlign: "center",
              padding: "0 24px",
            }}
          >
            {/* spinner */}
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: "3px solid rgba(223,204,37,0.25)",
                borderTopColor: "#dfcc25",
                animation: "hp-splash-spin 0.9s linear infinite",
              }}
            />
            {/* loading text */}
            <div
              style={{
                color: "#fff",
                fontFamily: "'Poiret One', serif",
                fontSize: 22,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                textShadow: "0 2px 12px rgba(0,0,0,0.4)",
              }}
            >
              Loading<span className="hp-splash-dots" />
            </div>
          </div>

          <style>{`
            @keyframes hp-splash-spin { to { transform: rotate(360deg); } }
            @keyframes hp-splash-dots {
              0%, 20%  { content: ""; }
              40%      { content: "."; }
              60%      { content: ".."; }
              80%, 100%{ content: "..."; }
            }
            .hp-splash-dots::after {
              content: "";
              display: inline-block;
              width: 1.5em;
              text-align: left;
              animation: hp-splash-dots 1.4s steps(1, end) infinite;
            }
          `}</style>
        </div>
      )}

    <div style={{ zIndex: 50, width: "100%" }} className="min-h-screen py-20">
      <div className="max-w-7xl mx-auto overflow-hidden">
      <SEO
        title={t("homeExtra.seo_title")}
        description="Shop jewellery, clothes, kitchen essentials, and makeup from Dar Oulmès. Local products, reasonable prices, and easy WhatsApp ordering."
        canonical="/"
        image="/og-image.jpg"
        jsonLd={jsonLd}
      />
        {/* ── HERO ── */}
<section className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden">
  
  {/* Background Images */}
  <div className="absolute inset-0">
    {heroSlideKeys.map((s, i) => (
      <img
        key={i}
        src={s.img}
        alt={`hero-${i}`}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          i === currentIndex ? "opacity-100" : "opacity-0"
        }`}
      />
    ))}
    {/* Dark overlay for contrast */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
    {/* Scan line effect */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.015) 2px, rgba(255,255,255,0.015) 4px)",
        zIndex: 1,
      }}
    />
  </div>

  {/* Corner decorations */}
  <div className="absolute top-8 left-8 w-12 h-12 border-t-2 border-l-2 border-white/40 z-10" />
  <div className="absolute top-8 right-8 w-12 h-12 border-t-2 border-r-2 border-white/40 z-10" />
  <div className="absolute bottom-20 left-8 w-12 h-12 border-b-2 border-l-2 border-white/40 z-10" />
  <div className="absolute bottom-20 right-8 w-12 h-12 border-b-2 border-r-2 border-white/40 z-10" />

  {/* Slide indicator dots */}
  <div className="absolute top-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
    {heroSlideKeys.map((_, i) => (
      <div
        key={i}
        className={`h-0.5 transition-all duration-500 ${
          i === currentIndex ? "w-8 bg-white" : "w-2 bg-white/30"
        }`}
      />
    ))}
  </div>

  {/* Main Content */}
  <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
    
    {/* Glowing top line */}
    <div
      className="w-24 h-px mb-8"
      style={{ background: "linear-gradient(90deg, transparent, rgba(255,182,193,0.8), transparent)" }}
    />

    {/* Small label */}
    <p
      className="text-white/50 uppercase tracking-[0.4em] text-xs mb-6 transition-all duration-700"
      style={{ fontFamily: "Poiret One" }}
    >
      {t(slide.subKey)}
    </p>

    {/* Big heading */}
    <h2
      className="text-white text-center font-bold mb-8 transition-all duration-700 leading-none"
      style={{
        fontFamily: "Poiret One",
        whiteSpace: "pre-line",
        fontSize: "clamp(3rem, 10vw, 100px)",
        textShadow: "0 0 80px rgba(255,182,193,0.3)",
      }}
    >
      {t(slide.headingKey)}
    </h2>

    {/* Glowing bottom line */}
    <div
      className="w-24 h-px mb-10"
      style={{ background: "linear-gradient(90deg, transparent, rgba(255,182,193,0.8), transparent)" }}
    />

    {/* CTA Button */}
    <div className="flex gap-2">
      <button
        className="group relative px-10 py-3 text-white transition-all duration-500 overflow-hidden"
        style={{
          fontFamily: "Poiret One",
          border: "1px solid rgba(255,255,255,0.25)",
          letterSpacing: "0.2em",
          fontSize: "13px",
          textTransform: "uppercase",
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.05)",
        }}
      >
        {/* Button hover fill */}
        <span
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: "rgba(255,182,193,0.2)" }}
        />
        {/* Corner accents on button */}
        <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-pink-300/60" />
        <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-pink-300/60" />
        <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-pink-300/60" />
        <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-pink-300/60" />
        <span className="relative">{t(slide.ctaKey)}</span>
      </button>
      
      {/* Video Dialog Button (replaces the second static button) */}
      <VideoDialog
        videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ" // Replace with your actual video URL
        buttonText={t("text.demo")}
      />
    </div>
  </div>

  {/* Scroll CTA */}
  <a
    href="#categories"
    className="absolute bottom-10 flex flex-col items-center gap-2 text-white/40 hover:text-white/80 transition-colors duration-300"
    style={{ fontFamily: "Poiret One", fontSize: "11px", letterSpacing: "0.3em", textTransform: "uppercase" }}
  >
    {t("hero.scroll_cta")}
    <div className="flex flex-col items-center gap-0.5 animate-bounce">
      <div className="w-px h-4 bg-white/30" />
      <ArrowDown size={14} />
    </div>
  </a>
</section>

        {/* ── CATEGORIES ── */}
        <section id="categories" className="px-6 pb-6">
          <h3 className="text-2xl text-center font-bold mt-10" style={{ fontFamily: "Poiret One" }}>
            {t("home.categories_title")}
          </h3>
          <CategoriesPage />
        </section>

        {/* ── FEATURED PRODUCTS ── */}
        <section className="px-6 pb-12">
          <h3 className="text-2xl text-center font-bold my-10" style={{ fontFamily: "Poiret One" }}>
            {t("home.featured")}
          </h3>

          <div className="relative">
            {/* left arrow */}
            <button
              onClick={() => scrollBy(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 -translate-x-4
                         w-10 h-10 rounded-full bg-[#1f3d3a] text-[#dfcc25] flex items-center justify-center
                         shadow-lg hover:scale-110 transition-transform"
            >
              <ChevronLeft size={20} />
            </button>

            {/* scrollable row */}
            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto pb-4 scroll-smooth"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {productsLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex-shrink-0 w-60 h-72 rounded-3xl bg-white/20 animate-pulse" />
                  ))
                : products.map((p) => {
                  const imgSrc = p.imageUrl?.startsWith("/uploads")
                    ? `${API}${p.imageUrl}`
                    : p.imageUrl || "https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=400&q=80";
                  return (
                    <div
                      key={p.id}
                      className="relative flex-shrink-0 w-60 bg-white/30 backdrop-blur-xl
                                 border border-white/20 rounded-3xl p-4
                                 shadow-[0_15px_35px_rgba(0,0,0,0.1)]
                                 transition-transform duration-500 hover:scale-105
                                 hover:shadow-[0_25px_50px_rgba(0,0,0,0.2)]"
                    >
                      {/* tag — show rating badge if rated */}
                      {p.rating > 0 && (
                        <span
                          className="absolute top-3 left-3 z-10 text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ bg: "#dfcc25", background: "#dfcc25", color: "#1f3d3a" }}
                        >
                          ★ {Number(p.rating).toFixed(1)}
                        </span>
                      )}

                      {/* image */}
                      <div className="overflow-hidden rounded-2xl">
                        <img
                          src={imgSrc}
                          alt={p.name}
                          className="h-44 w-full object-cover transition-transform duration-500 hover:scale-110"
                        />
                      </div>

                      {/* info */}
                      <div className="mt-4">
                        <h4 className="text-base font-semibold text-gray-900 truncate">{p.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {Number(p.price).toFixed(2)} {t("product.currency")}
                        </p>
                      </div>

                      {/* CTA */}
                      <button
                        className="mt-4 w-full py-2 rounded-full bg-[#1f3d3a]
                                   text-[#dfcc25] font-medium shadow-lg hover:shadow-xl
                                   hover:scale-105 transition-all duration-300 text-sm"
                      >
                        {t("product.add_to_cart")}
                      </button>

                      {/* glow accent */}
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-tr from-purple-400 via-pink-400 to-yellow-400 rounded-full blur-xl opacity-40 pointer-events-none" />
                    </div>
                  );
                })}
            </div>

            {/* right arrow */}
            <button
              onClick={() => scrollBy(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 translate-x-4
                         w-10 h-10 rounded-full bg-[#1f3d3a] text-[#dfcc25] flex items-center justify-center
                         shadow-lg hover:scale-110 transition-transform"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </section>

        {/* ── WHY DAR OULMES ── */}
        <section className="relative py-32 overflow-hidden" style={{ background: "#0a0f0e" }}>
        
          {/* Full background image with dark overlay */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&q=80"
              alt=""
              className="w-full h-full object-cover opacity-10"
            />
            {/* Scan lines */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.012) 2px, rgba(255,255,255,0.012) 4px)",
              }}
            />
            {/* Radial vignette */}
            <div
              className="absolute inset-0"
              style={{
                background: "radial-gradient(ellipse 80% 60% at 50% 50%, transparent 30%, rgba(10,15,14,0.9) 100%)",
              }}
            />
          </div>
        
          {/* Corner brackets — section level */}
          <div className="absolute top-8 left-8 w-10 h-10 pointer-events-none" style={{ borderTop: "1.5px solid rgba(223,204,37,0.4)", borderLeft: "1.5px solid rgba(223,204,37,0.4)" }} />
          <div className="absolute top-8 right-8 w-10 h-10 pointer-events-none" style={{ borderTop: "1.5px solid rgba(223,204,37,0.4)", borderRight: "1.5px solid rgba(223,204,37,0.4)" }} />
          <div className="absolute bottom-8 left-8 w-10 h-10 pointer-events-none" style={{ borderBottom: "1.5px solid rgba(223,204,37,0.4)", borderLeft: "1.5px solid rgba(223,204,37,0.4)" }} />
          <div className="absolute bottom-8 right-8 w-10 h-10 pointer-events-none" style={{ borderBottom: "1.5px solid rgba(223,204,37,0.4)", borderRight: "1.5px solid rgba(223,204,37,0.4)" }} />
        
          <div className="relative max-w-6xl mx-auto px-6">
        
            {/* ── HEADER ── */}
            <div className="text-center mb-20">
        
              {/* Eyebrow */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-px w-12" style={{ background: "linear-gradient(90deg, transparent, rgba(200,169,138,0.6))" }} />
                <p style={{
                  fontFamily: "Poiret One",
                  fontSize: "10px",
                  letterSpacing: "0.5em",
                  textTransform: "uppercase",
                  color: "#c8a98a",
                }}>
                  {t("why.eyebrow")}
                </p>
                <div className="h-px w-12" style={{ background: "linear-gradient(90deg, rgba(200,169,138,0.6), transparent)" }} />
              </div>
        
              {/* Main title */}
              <h2
                style={{
                  fontFamily: "Poiret One",
                  fontSize: "clamp(3rem, 8vw, 6rem)",
                  fontWeight: 700,
                  color: "#ffffff",
                  lineHeight: 1,
                  marginBottom: "24px",
                  textShadow: "0 0 80px rgba(223,204,37,0.2)",
                }}
              >
                {t("why.title")}
              </h2>
        
              {/* Gold divider */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <div style={{ width: "40px", height: "1px", background: "rgba(223,204,37,0.5)" }} />
                <div style={{ width: "6px", height: "6px", background: "#dfcc25", transform: "rotate(45deg)" }} />
                <div style={{ width: "6px", height: "6px", background: "#dfcc25", transform: "rotate(45deg)" }} />
                <div style={{ width: "40px", height: "1px", background: "rgba(223,204,37,0.5)" }} />
              </div>
        
              <p style={{
                fontFamily: "Poiret One",
                fontSize: "16px",
                color: "rgba(255,255,255,0.45)",
                maxWidth: "480px",
                margin: "0 auto",
                lineHeight: 1.8,
                letterSpacing: "0.05em",
              }}>
                {t("why.description")}
              </p>
            </div>
        
            {/* ── CARDS ── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {whyCards.map((card, i) => (
                <WhyCard key={card.titleKey} card={card} i={i} t={t} />
              ))}
            </div>
          </div>
        </section>
        {/* ── FOOTER ── */}
        <footer>
          {/* Trust badges strip */}
          <div className="relative overflow-hidden" style={{ background: "#f3eee9" }}>
            <div
              className="h-px w-full"
              style={{
                background:
                  "linear-gradient(90deg,transparent,#dfcc25 30%,#c8a98a 70%,transparent)",
              }}
            />
        
            <div className="grid grid-cols-2 md:grid-cols-4">
              {trustBadges.map(({ Icon, labelKey }, i) => (
                <TrustBadgeItem
                  key={labelKey}
                  Icon={Icon}
                  labelKey={labelKey}
                  t={t}
                  isLast={i === trustBadges.length - 1}
                />
              ))}
            </div>
          </div>
        
          {/* Main footer */}
          <div className="relative overflow-hidden px-6 pt-16 pb-8" style={{ background: "#1f3d3a" }}>
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background:
                  "linear-gradient(90deg,transparent,#dfcc25 30%,#c8a98a 70%,transparent)",
              }}
            />
        
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)
                `,
                backgroundSize: "48px 48px",
              }}
            />
        
            <div
              className="absolute -right-24 -top-24 w-72 h-72 rounded-full blur-3xl opacity-20"
              style={{ background: "#dfcc25" }}
            />
        
            <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
              {/* Brand */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-10 h-10 flex items-center justify-center shadow-lg"
                    style={{
                      background: "linear-gradient(135deg,#dfcc25,#c8a98a)",
                      clipPath:
                        "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    }}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ background: "#1f3d3a" }} />
                  </div>
        
                  <h4
                    className="text-3xl font-bold"
                    style={{ fontFamily: "Poiret One", color: "#dfcc25" }}
                  >
                    Dar Oulmes
                  </h4>
                </div>
        
                <div
                  className="h-px mb-5"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(223,204,37,0.45), transparent)",
                  }}
                />
        
                <p
                  className="text-sm leading-relaxed max-w-sm"
                  style={{
                    color: "rgba(255,255,255,0.62)",
                    fontFamily: "Poiret One",
                  }}
                >
                  {t("footer.brand_desc")}
                </p>
              </div>
        
              {/* Quick links */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-4 rounded-full" style={{ background: "#dfcc25" }} />
                  <h5
                    className="text-xs uppercase tracking-[0.28em] font-semibold"
                    style={{ color: "#c8a98a", fontFamily: "Poiret One" }}
                  >
                    {t("footer.quick_links")}
                  </h5>
                </div>
        
                <div className="space-y-3">
                  {quickLinks.map(({ labelKey, href }) => (
                    <a
                      key={labelKey}
                      href={href}
                      className="group flex items-center gap-3 text-sm transition-all duration-300 hover:translate-x-1"
                      style={{
                        color: "rgba(255,255,255,0.62)",
                        fontFamily: "Poiret One",
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rotate-45 transition-all duration-300 group-hover:scale-150"
                        style={{ background: "#dfcc25", opacity: 0.65 }}
                      />
                      <span className="transition-colors duration-300 group-hover:text-[#dfcc25]">
                        {t(labelKey)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
        
              {/* Contact */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-4 rounded-full" style={{ background: "#c8a98a" }} />
                  <h5
                    className="text-xs uppercase tracking-[0.28em] font-semibold"
                    style={{ color: "#c8a98a", fontFamily: "Poiret One" }}
                  >
                    {t("footer.contact_us")}
                  </h5>
                </div>
        
                <div className="space-y-3 mb-7">
                  <a
                    href="tel:+212642186606"
                    className="group flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-300 hover:bg-white/5"
                    style={{
                      border: "1px solid rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.68)",
                      fontFamily: "Poiret One",
                    }}
                  >
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-full text-xs"
                      style={{ background: "rgba(223,204,37,0.12)", color: "#dfcc25" }}
                    >
                      ☎
                    </span>
                    <span className="group-hover:text-[#dfcc25] transition-colors duration-300">
                      +212 642 186 606
                    </span>
                  </a>
        
                  <a
                    href="mailto:mohameddhimni311@gmail.com"
                    className="group flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-300 hover:bg-white/5"
                    style={{
                      border: "1px solid rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.68)",
                      fontFamily: "Poiret One",
                    }}
                  >
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-full text-xs"
                      style={{ background: "rgba(200,169,138,0.14)", color: "#c8a98a" }}
                    >
                      ✉
                    </span>
                    <span className="break-all group-hover:text-[#dfcc25] transition-colors duration-300">
                      mohameddhimni311@gmail.com
                    </span>
                  </a>
                </div>
        
                {/* WhatsApp CTA */}
                <a
                  href="https://wa.me/212642186606"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex items-center gap-3 px-6 py-3 text-sm font-semibold overflow-hidden transition-all duration-300 hover:shadow-xl"
                  style={{
                    border: "1px solid rgba(223,204,37,0.45)",
                    color: "#dfcc25",
                    fontFamily: "Poiret One",
                    clipPath:
                      "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
                  }}
                >
                  <span
                    className="absolute inset-0 translate-x-[-105%] group-hover:translate-x-0 transition-transform duration-500"
                    style={{ background: "#dfcc25" }}
                  />
                  <BsWhatsapp
                    size={17}
                    className="relative z-10 transition-colors duration-300 group-hover:text-[#1f3d3a]"
                  />
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-[#1f3d3a]">
                    {t("footer.whatsapp_cta")}
                  </span>
                </a>
              </div>
            </div>
        
            {/* Bottom bar */}
            <div
              className="relative max-w-6xl mx-auto mt-14 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs"
              style={{
                borderTop: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.35)",
                fontFamily: "Poiret One",
              }}
            >
              <span>{t("footer.copyright")}</span>
        
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rotate-45" style={{ background: "rgba(223,204,37,0.45)" }} />
                <span>{t("footer.made_with_love")}</span>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </div>
    </>
  );
}